/**
 * t222u: the ⋯ More button at the right end of the phone tab bar opens a menu of phone destinations that aren't tabs.
 * Only places the person can open; nothing that duplicates one of their tabs. Pure TS (web and native).
 */
import type { MobileRole, MobileSlot, MobileTabId } from './shell';
import { MOBILE_TAB_PATH, mobileTabForPath } from './shell';
import { ON_SITE_PERMS } from '../workforce/visitors';
import { DASHBOARD_PERMS } from '../workforce/dashboard';
import { pathAvailable, type ModuleId } from '../modules';
import { FLEET_JOURNEY_PERMS, FLEET_TRACKING_PERMS, FLEET_VEHICLE_PERMS } from './fleet';

export type MoreItemId = 'dashboard' | 'people' | 'buildings' | 'issues' | 'equipment' | 'scan' | 'visitors' | 'training' | 'policies' | 'announcements' | 'calendar' | 'timeoff' | 'approvals' | 'fleet-dashboard' | 'journeys' | 'live' | 'vehicles' | 'runs' | 'help';
export interface MoreItem { id: MoreItemId; label: string; to: string }
type Holds = (perm: string) => boolean;

const ALL: (MoreItem & { show?: (role: MobileRole, holds: Holds) => boolean })[] = [
  // t335u item 7: the manager dashboard, first for anyone who runs a building area.
  // t554u: the Dashboard also holds Store sales (sales.view).
  { id: 'dashboard', label: 'Dashboard', to: '/dashboard', show: (_r, holds) => DASHBOARD_PERMS.some(holds) || holds('sales.view') },
  { id: 'people', label: 'People', to: '/people' },
  { id: 'buildings', label: 'Buildings', to: '/buildings' },
  { id: 'issues', label: 'My issues', to: '/incidents' },
  // t335u: the equipment register, for anyone who can report issues (pre-use checks, defects) or runs the register.
  { id: 'equipment', label: 'Equipment', to: '/equipment', show: (_r, holds) => ['ops.incident.report', 'ops.incident.settings', 'ops.checklist.template.manage'].some(holds) },
  // t627u: Scan, the one camera for every Hub QR code (equipment labels, places, checklists), for everyone.
  { id: 'scan', label: 'Scan', to: '/scan' },
  // t335u: on site now and the fire roll call, for the building's managers.
  { id: 'visitors', label: 'On site', to: '/visitors', show: (_r, holds) => ON_SITE_PERMS.some(holds) },
  // t335u: training courses (articles + quiz); everyone may have some assigned.
  { id: 'training', label: 'Training', to: '/training' },
  // t563u: policies to read and acknowledge, and approvals waiting for you.
  { id: 'policies', label: 'Policies', to: '/policies' },
  { id: 'announcements', label: 'Announcements', to: '/announcements' },
  { id: 'calendar', label: 'Calendar', to: '/calendar' },
  // t354u: balance, holiday year and every absence (also from My shifts › Time off).
  { id: 'timeoff', label: 'Time off', to: '/workforce/time-off' },
  { id: 'approvals', label: 'Approvals', to: '/rota/approvals', show: role => role === 'manager' },
  // t443u: the fleet's journeys and vehicles, for anyone who can see them (drivers have My runs).
  // t636u: Fleet › Dashboard (upcoming journeys, live map, quick tiles), for anyone with fleet.view. Web only for now.
  { id: 'fleet-dashboard', label: 'Fleet dashboard', to: '/fleet/dashboard', show: (_r, holds) => holds('fleet.view') },
  { id: 'journeys', label: 'Journeys', to: '/fleet/journeys', show: (_r, holds) => FLEET_JOURNEY_PERMS.some(holds) },
  { id: 'live', label: 'Live map', to: '/fleet/live', show: (_r, holds) => FLEET_TRACKING_PERMS.some(holds) },
  { id: 'vehicles', label: 'Vehicles', to: '/fleet/vehicles', show: (_r, holds) => FLEET_VEHICLE_PERMS.some(holds) },
  { id: 'runs', label: 'My runs', to: '/fleet/my-runs', show: (_r, holds) => holds('fleet.drive') },
  { id: 'help', label: 'Help', to: '/help' },
];

/** t369u: `off` leaves out destinations whose module is switched off for the workspace. */
export function moreItems(role: MobileRole, holds: Holds, slots: MobileSlot[], off?: readonly ModuleId[]): MoreItem[] {
  const tabPaths = new Set(slots.filter(s => s !== 'create').map(s => MOBILE_TAB_PATH[s as MobileTabId]));
  return ALL.filter(i => (!i.show || i.show(role, holds)) && !tabPaths.has(i.to) && pathAvailable(off, i.to)).map(({ id, label, to }) => ({ id, label, to }));
}
/** The More button reads as active on its own destinations. */
export const isMorePath = (path: string, items: MoreItem[]) => items.some(i => path === i.to || path.startsWith(`${i.to}/`));
/** The lit tab: ⋯ alone on its destinations (Buildings, People, Calendar…), never ⋯ and a tab together. */
/** t354u: More items that also sit under a tab (Time off is My shifts › Time off): that tab stays lit. */
const UNDER_A_TAB = ['/workforce/time-off'];
export const activeMobileTab = (path: string, slots: readonly MobileSlot[], items: MoreItem[]): MobileTabId | null =>
  isMorePath(path, items) && !UNDER_A_TAB.some(p => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)) ? null : mobileTabForPath(path, slots);

/**
 * B3: a page opened from a card or link elsewhere (an event's detail from Home or the feed) keeps the tab it was
 * opened from lit. It lights ⋯ only when opened from a ⋯ destination (the Calendar list, reached from the ⋯ menu),
 * or when there's nowhere it came from (a push or a pasted link).
 */
export const keepsOriginTab = (path: string) => /^\/calendar\/events\/[^/]+/.test(path);
export const activeMobileTabFrom = (path: string, slots: readonly MobileSlot[], items: MoreItem[], from?: MobileTabId | null): MobileTabId | null =>
  keepsOriginTab(path) && from !== undefined ? from : activeMobileTab(path, slots, items);
